import React, { createContext, useContext, useState, useCallback } from "react";

const CLIENT_STORAGE_KEY = "swa-poc-client-data";

/** Drops the stored client without a React update. Logout navigates away, and a state update would render the landing page first, which writes the server theme back. */
export function clearStoredClient(): void {
  sessionStorage.removeItem(CLIENT_STORAGE_KEY);
}

export interface MenuItem {
  path: string;
  label: string;
}

export interface PersonMetadata {
  email: string;
  name?: string;
  [key: string]: unknown;
}

export interface ClientData {
  email: string;
  personMetadata: PersonMetadata;
  roles?: string[];
  features?: string[];
  menus?: MenuItem[];
}

interface ClientContextValue {
  client: ClientData | null;
  setClient: (data: ClientData | null) => void;
  clearClient: () => void;
}

const ClientContext = createContext<ClientContextValue | undefined>(undefined);

// Load client data from sessionStorage
function loadClientFromStorage(): ClientData | null {
  try {
    const stored = sessionStorage.getItem(CLIENT_STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored);
    // Basic validation
    if (parsed && typeof parsed.email === "string" && parsed.personMetadata) {
      return parsed as ClientData;
    }
    return null;
  } catch {
    // Invalid data in storage, clear it
    sessionStorage.removeItem(CLIENT_STORAGE_KEY);
    return null;
  }
}

// Save client data to sessionStorage
function saveClientToStorage(data: ClientData | null): void {
  if (data) {
    sessionStorage.setItem(CLIENT_STORAGE_KEY, JSON.stringify(data));
  } else {
    sessionStorage.removeItem(CLIENT_STORAGE_KEY);
  }
}

export const ClientContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize from sessionStorage
  const [client, setClientState] = useState<ClientData | null>(() => loadClientFromStorage());

  // Wrapper that also persists to sessionStorage
  const setClient = useCallback((data: ClientData | null) => {
    setClientState(data);
    saveClientToStorage(data);
  }, []);

  // Explicit clear function (useful for logout)
  const clearClient = useCallback(() => {
    setClientState(null);
    sessionStorage.removeItem(CLIENT_STORAGE_KEY);
  }, []);

  return (
    <ClientContext.Provider
      value={{
        client,
        setClient,
        clearClient,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClientContext = (): ClientContextValue => {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error("useClientContext must be used inside ClientContextProvider");
  return ctx;
};
